import React, { ReactNode } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SponsorData } from '~admin/model/sponsor-data';
import { SponsorPicker } from '~admin/view/components/details/sponsor-details/sponsor-picker.component';
import { SponsorPreview } from '~admin/view/components/details/sponsor-details/sponsor-preview.component';
import { NumberField } from '~admin/view/components/ui/form/fields/field-number.component';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { useModalControls } from '~common/hook/use-modal-controls';
import { Color } from '~common/model/basics';
import { DeleteIcon } from '~web/view/components/ui/icon/delete.icon';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface Props {
  name: string;
  onRemove?: VoidFunction;
}

export interface EventSponsorGroupFormData {
  height: number;
  sponsors: SponsorData[];
}

export const SponsorDetailsGroup: React.FunctionComponent<Props> = ({ name, onRemove }) => {
  const { t } = useTranslation();
  const [pickerShown, showPicker, hidePicker] = useModalControls();

  return (
    <Container>
      <Top>
        <RestrictBox>
          <NumberField
            name={`${name}.height`}
            label={t('details.sponsor.groups.height')}
            endAdornment={'px'}
          />
        </RestrictBox>
        <DeleteButton
          color={Color.White}
          size={Size.Small}
          onClick={(): void => {
            onRemove?.();
          }}
        >
          <DeleteIcon color={Color.Black} />
        </DeleteButton>
      </Top>
      <FieldArray name={`${name}.sponsors`}>
        {({ fields }): ReactNode => {
          const onRemove = (index: number): void => {
            fields.remove(index);
          };

          const onSponsorSelected = (sponsor: SponsorData): void => {
            const sponsorAlreadyAdded = Boolean(
              fields.value.find((value: SponsorData) => value.id === sponsor.id),
            );
            if (!sponsorAlreadyAdded) {
              fields.push(sponsor);
            }
            hidePicker();
          };

          return (
            <Sponsors>
              {fields.map((name: string, index: number) => (
                <SponsorPreview key={name} name={name} onRemove={(): void => onRemove(index)} />
              ))}
              <Box>
                <AddSponsorButton onClick={showPicker}>
                  <PlusIcon />
                </AddSponsorButton>
              </Box>
              {pickerShown && (
                <SponsorPicker onCancel={hidePicker} onSponsorSelected={onSponsorSelected} />
              )}
            </Sponsors>
          );
        }}
      </FieldArray>
    </Container>
  );
};

const Container = styled(Column)`
  width: 100%;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;
  align-items: flex-start;
  margin-top: 24px;

  > div:not(:first-child) {
    margin-top: 16px;
  }
`;

const Top = styled(Box)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const Sponsors = styled(Box)`
  flex-wrap: wrap;

  > div:not(:last-child) {
    margin-right: 8px;
  }
`;

const AddSponsorButton = styled(Box)`
  height: 150px;
  width: 150px;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  margin-top: 16px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin-right: 32px;
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 44px;
    width: 44px;
    height: 44px;
    padding: 0;
  }
`;



// WEBPACK FOOTER //
// ./view/components/details/sponsor-details/sponsor-details-group.component.tsx